import { useSelector } from 'react-redux';
import { selectDocsFormData } from '../../redux-state/selectors';
import { TRANSACTION_TYPE_MAP } from '../docs/constants';
import { formatDate } from '../utility/formatDate';

/**
 * Docs output — Transaction Information.
 *
 * This section is kept field-for-field and column-for-column identical to the
 * Transaction Information block in `utility/generateDocsReport.js`. The screen
 * previously omitted Plate Type entirely, listed the remaining rows in a
 * different order than the PDF, and printed `protectionPackages` under a
 * "Theft Protection" heading (so Protection Packages appeared twice with the
 * same amount while the real theft-protection figure never showed).
 *
 * If you add, remove or reorder a row here, make the same change in
 * generateDocsReport.js so the download keeps matching the screen.
 */
const currency = (value) =>
  Number(value || 0).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  });

// Defined at module scope: a component declared inside the render body is a new
// component type on every render, which makes React remount the whole subtree.
const Row = ({ label, children }) => (
  <div className='flex flex-row justify-between gap-2 items-start'>
    <div>{label}</div>
    <div className='font-medium text-right break-words min-w-0'>{children}</div>
  </div>
);

// `value && <Row/>` renders a stray "0" when value is the number 0, so guards
// below go through this instead.
const present = (value) => value !== undefined && value !== null && value !== '' && value !== 0;

function TransactionInformation() {
  const docsFormData = useSelector(selectDocsFormData) || {};

  return (
    <div>
      <div className='border-t-2 border-t-gray-100 mb-3'>
        <div className='text-gray-700 mt-6 font-bold'>
          Transaction Information
        </div>
      </div>

      <div className='flex flex-col lg:flex-row w-full gap-6 lg:gap-8 mb-6'>
        <div className='flex flex-col w-full min-w-0 text-gray-700 gap-2'>
          {present(docsFormData?.transactionType) && (
            <Row label='Transaction Type:'>
              {TRANSACTION_TYPE_MAP[docsFormData.transactionType] || docsFormData.transactionType}
            </Row>
          )}

          {present(docsFormData?.uploadDate) && (
            <Row label='Uploaded Date:'>{formatDate(docsFormData.uploadDate)}</Row>
          )}

          {present(docsFormData?.plateNumber) && (
            <Row label='Plate Number:'>{docsFormData.plateNumber}</Row>
          )}

          {present(docsFormData?.docsFees) && (
            <Row label='Doc Fees:'>{currency(docsFormData.docsFees)}</Row>
          )}

          {present(docsFormData?.extendedWarranties) && (
            <Row label='Extended Warranties:'>{currency(docsFormData.extendedWarranties)}</Row>
          )}

          {present(docsFormData?.protectionPackages) && (
            <Row label='Protection Packages:'>{currency(docsFormData.protectionPackages)}</Row>
          )}

          <Row label='Military:'>{docsFormData?.military ? 'Yes' : 'No'}</Row>

          <Row label='Lien:'>{docsFormData?.lien ? 'Yes' : 'No'}</Row>
        </div>

        <div className='flex flex-col w-full min-w-0 text-gray-700 gap-2'>
          {present(docsFormData?.transactionDate) && (
            <Row label='Transaction Date:'>{formatDate(docsFormData.transactionDate)}</Row>
          )}

          {present(docsFormData?.plateType) && (
            <Row label='Plate Type:'>{docsFormData.plateType}</Row>
          )}

          {present(docsFormData?.basePrice) && (
            <Row label='Base Price:'>{currency(docsFormData.basePrice)}</Row>
          )}

          {present(docsFormData?.additionalFees) && (
            <Row label='Additional Fee:'>{currency(docsFormData.additionalFees)}</Row>
          )}

          {present(docsFormData?.insurancePackages) && (
            <Row label='Insurance Packages:'>{currency(docsFormData.insurancePackages)}</Row>
          )}

          {present(docsFormData?.theftProtection) && (
            <Row label='Theft Protection:'>{currency(docsFormData.theftProtection)}</Row>
          )}

          <Row label='Plate Transfer:'>{docsFormData?.plateTransfer ? 'Yes' : 'No'}</Row>

          {present(docsFormData?.driverCategory) && (
            <Row label='Driver Category:'>{docsFormData.driverCategory}</Row>
          )}
        </div>
      </div>
    </div>
  )
}

export default TransactionInformation
